/**
 * Source de vérité unique pour la configuration des plans Luminjo.
 *
 * IMPORTANT — duplication contrôlée :
 * Les Edge Functions Supabase ne peuvent pas importer depuis `src/`.
 * Le pendant Deno de ce fichier vit dans `supabase/functions/_shared/plans-shared.ts`
 * et DOIT rester aligné sur celui-ci. Toute évolution se fait dans les deux fichiers.
 */

export type PlanKey = 'free' | 'captain' | 'wonder' | 'super';

export const PLAN_KEYS: PlanKey[] = ['free', 'captain', 'wonder', 'super'];

export const PLAN_DISPLAY_NAMES: Record<PlanKey, string> = {
  free: 'Jo',
  captain: 'Captain Jo',
  wonder: 'Wonder Jo',
  super: 'Super Jo',
};

/** Stripe Product IDs (production) */
export const STRIPE_PRODUCT_IDS: Record<Exclude<PlanKey, 'free'>, string> = {
  captain: 'prod_Oa5VxUWoXP6rpV',
  wonder: 'prod_Oa5VhBpHpO4Pgj',
  super: 'prod_Oa5VI2eQzbi6cF',
};

/** Stripe Price IDs (production) */
export const STRIPE_PRICE_IDS: Record<Exclude<PlanKey, 'free'>, { monthly: string; yearly: string }> = {
  captain: {
    monthly: 'price_1NmvKWFimF6327ixz5cRwTBR',
    yearly: 'price_1NmvKWFimF6327ixx7skIpXu',
  },
  wonder: {
    monthly: 'price_1NmvKcFimF6327ixA3U1Jd19',
    yearly: 'price_1NmvKcFimF6327ixLkaypkdy',
  },
  super: {
    monthly: 'price_1NmvKaFimF6327ixMbG2nWr5',
    yearly: 'price_1NmvKaFimF6327ixOrPzknP1',
  },
};

/** Limites par défaut (fallback si la table `plan_limits_config` est vide) */
export const DEFAULT_PLAN_LIMITS: Record<PlanKey, { tickets: number; agents: number; aiPoints: number }> = {
  free: { tickets: 10, agents: 1, aiPoints: 50 },
  captain: { tickets: 300, agents: 3, aiPoints: 500 },
  wonder: { tickets: 1000, agents: 10, aiPoints: 1000 },
  super: { tickets: 5000, agents: 20, aiPoints: 2000 },
};

/** TVA et remise annuelle (centralisés pour i18n future) */
export const VAT_RATE = 0.20;
export const ANNUAL_DISCOUNT_LABEL = '-8%';

const PRODUCT_TO_PLAN: Record<string, PlanKey> = Object.entries(STRIPE_PRODUCT_IDS).reduce(
  (acc, [plan, productId]) => ({ ...acc, [productId]: plan as PlanKey }),
  {} as Record<string, PlanKey>
);

export function getPlanFromProductId(productId: string | null | undefined): PlanKey {
  if (!productId) return 'free';
  return PRODUCT_TO_PLAN[productId] ?? 'free';
}

export function isPaidPlan(plan: string | null | undefined): boolean {
  return !!plan && plan !== 'free';
}

export function getPlanDisplayName(plan: string | null | undefined): string {
  if (!plan) return PLAN_DISPLAY_NAMES.free;
  return PLAN_DISPLAY_NAMES[plan as PlanKey] ?? PLAN_DISPLAY_NAMES.free;
}
